import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack, Group, Card, Text, Switch, Center, Loader, Alert, Box, ActionIcon, Button, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ChevronUp, ChevronDown, RotateCcw } from 'lucide-react';

import { gameTypesApi, type GameType } from '../api';
import { modeMeta } from '../components/modeMeta';

export default function GameTypesPage() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const types = useQuery({ queryKey: ['gameTypes'], queryFn: gameTypesApi.getAll });

  const ordered = useMemo(() => {
    return [...(types.data ?? [])].sort(
      (a, b) => (a.display_order ?? a.default_order) - (b.display_order ?? b.default_order)
    );
  }, [types.data]);

  const push = useMutation({
    mutationFn: (list: GameType[]) =>
      gameTypesApi.updatePreferences(
        list.map((t, i) => ({
          game_type_id: t.id,
          enabled: t.enabled ?? t.default_enabled,
          display_order: i,
        }))
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gameTypes'] }),
    onError: (e: any) =>
      notifications.show({ title: 'Uložení se nepovedlo', message: e?.response?.data?.error || e.message, color: 'red' }),
    onSettled: () => setBusy(false),
  });

  const reset = useMutation({
    mutationFn: gameTypesApi.resetPreferences,
    onSuccess: () => {
      notifications.show({ message: 'Obnoveno výchozí nastavení' });
      qc.invalidateQueries({ queryKey: ['gameTypes'] });
    },
    onError: (e: any) =>
      notifications.show({ title: 'Obnovení se nepovedlo', message: e?.response?.data?.error || e.message, color: 'red' }),
  });

  const toggle = (id: string) => {
    setBusy(true);
    push.mutate(
      ordered.map((t) => (t.id === id ? { ...t, enabled: !(t.enabled ?? t.default_enabled) } : t))
    );
  };

  const move = (index: number, delta: number) => {
    const next = [...ordered];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setBusy(true);
    push.mutate(next);
  };

  if (types.isLoading) return <Center h={240}><Loader /></Center>;
  if (types.error) {
    return <Alert color="red" title="Herní módy se nepodařilo načíst">{(types.error as any)?.message}</Alert>;
  }

  const enabledCount = ordered.filter((t) => t.enabled ?? t.default_enabled).length;

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text c="dimmed" fz="sm">
          Pořadí určuje, jak se módy nabízejí v mobilní aplikaci.
        </Text>
        <Button
          variant="default"
          leftSection={<RotateCcw size={14} />}
          loading={reset.isPending}
          onClick={() => {
            if (window.confirm('Obnovit výchozí zapnutí i pořadí všech módů?')) reset.mutate();
          }}
        >
          Výchozí nastavení
        </Button>
      </Group>

      <Card padding={0}>
        <Group p="sm" justify="space-between" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Text fw={700} fz="sm">Herní módy</Text>
          <Text c="dimmed" fz="sm">{enabledCount} z {ordered.length} zapnuto</Text>
        </Group>

        {ordered.map((t, i) => {
          const meta = modeMeta(t.id);
          const Icon = meta.icon;
          const on = t.enabled ?? t.default_enabled;
          return (
            <Group
              key={t.id}
              p="sm"
              gap="sm"
              wrap="nowrap"
              style={{ borderBottom: i === ordered.length - 1 ? 'none' : '1px solid var(--mantine-color-default-border)' }}
            >
              <Stack gap={0}>
                <Tooltip label="Posunout nahoru">
                  <ActionIcon size="sm" disabled={i === 0 || busy} onClick={() => move(i, -1)} aria-label="Posunout nahoru">
                    <ChevronUp size={13} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Posunout dolů">
                  <ActionIcon size="sm" disabled={i === ordered.length - 1 || busy} onClick={() => move(i, 1)} aria-label="Posunout dolů">
                    <ChevronDown size={13} />
                  </ActionIcon>
                </Tooltip>
              </Stack>
              <Icon size={16} color={`var(--mantine-color-${meta.color}-6)`} style={{ flex: '0 0 16px' }} />
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text fw={650} fz="sm">{t.name || meta.label}</Text>
                {t.description && <Text c="dimmed" fz="sm">{t.description}</Text>}
              </Box>
              <Switch checked={on} disabled={busy} onChange={() => toggle(t.id)} aria-label={t.name || meta.label} />
            </Group>
          );
        })}
      </Card>

      <Text c="dimmed" fz="xs">
        Vypnutý mód zmizí z mobilní aplikace a nepůjde pro něj založit novou hru.
        Na už odehrané hry to nemá vliv.
      </Text>
    </Stack>
  );
}
